<template>
  <Modal
    v-if="modalVisible"
    :value="modalVisible"
    @input="modalVisible = $event"
    dataTestId="review-submission-modal"
  >
    <div v-if="isLoading" class="d-flex justify-center align-center" style="min-height: 300px">
      <v-progress-circular indeterminate color="brandPrimary" />
    </div>
    <div v-else class="container">
      <div class="header">
        <Typography variant="h-2">Submission ID {{ formSubmissionId }}</Typography>
        <ItemsChip :numberOfItems="needsActionClaims.length" />
      </div>
      <div class="claim-info">
        <ClaimDates :claim="needsActionClaims[0]" />
        <ClaimCustomer :claim="needsActionClaims[0]" />
        <ClaimSubmitterDetails :claim="needsActionClaims[0]" />
      </div>
      <div class="items-needing-action">
        <div class="items-needing-action-header">
          <v-icon medium color="warning">mdi-alert</v-icon>
          <Typography variant="h-2"
            >Items Needing Action ({{ needsActionClaims.length }})</Typography
          >
        </div>
        <NeedsActionClaim
          v-for="claim in needsActionClaims"
          :key="claim.formSubmissionEntryId"
          :claim="claim"
        />
      </div>
      <v-expansion-panels v-if="approvedClaims && approvedClaims.length > 0" flat>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <Typography variant="h-5">Approved Items ({{ approvedClaims.length }})</Typography>
            <template v-slot:actions>
              <v-icon medium> mdi-chevron-down </v-icon>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="approved-claims">
              <ClaimCard
                v-for="claim in approvedClaims"
                :key="`${claim.formSubmissionEntryId}-${claim.programId}`"
                :claim="claim"
              />
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <div class="documentation-container">
        <Typography variant="h-2">Documentation</Typography>
        <FormFileList
          :attachmentsItem="formAttachments"
          :hasAdditionalClaimants="hasAdditionalClaimants"
        />
        <FileInput
          :value="newAttachments"
          @input="newAttachments = $event"
          label="Upload a file or photo*"
          hint="A max file size of 50 MB is supported. Upload up to 5 files."
          @isValid="onValidationChange"
          multiple
          :maxFiles="5"
        />
      </div>
      <div class="text-center" v-if="hasAdditionalClaimants">
        <Typography variant="caption-1"
          ><strong>Note: You'll be resubmitting on behalf of all claimants.</strong></Typography
        >
      </div>
      <div class="align-self-center">
        <Button :disabled="!canSubmit" :loading="isSubmitting" @click="onSubmit">Submit</Button>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from "@/components/generic/Modal.vue";
import Typography from "@/components/generic/Typography.vue";
import ItemsChip from "./ItemsChip.vue";
import ClaimCard from "./claims/ClaimCard.vue";
import FileInput from "@/components/forms/FileInput.vue";
import Button from "@/components/generic/Button.vue";
import ClaimDates from "./claims/claim-info/ClaimDates.vue";
import ClaimCustomer from "./claims/claim-info/ClaimCustomer.vue";
import ClaimSubmitterDetails from "./claims/claim-info/ClaimSubmitterDetails.vue";
import NeedsActionClaim from "./claims/NeedsActionClaim.vue";
import FormFileList from "@/components/forms/FormFileList.vue";
import ProgramClaimService from "@/services/ProgramClaimService";
import { mapState } from "vuex";
import formFileUploadMixin from "@/mixins/forms/formFileUploadMixin";

export default {
  name: "ReviewSubmissionModal",
  components: {
    Modal,
    Typography,
    ClaimDates,
    ClaimCustomer,
    ClaimSubmitterDetails,
    ItemsChip,
    NeedsActionClaim,
    ClaimCard,
    FileInput,
    Button,
    FormFileList,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    formSubmissionId: {
      type: Number,
      required: true,
    },
    needsActionClaims: {
      type: Array,
      required: true,
    },
    formAttachments: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      canSubmit: false,
      newAttachments: null,
      approvedClaims: null,
      isLoading: false,
      isSubmitting: false,
    };
  },
  computed: {
    ...mapState(["userProfile"]),
    hasAdditionalClaimants() {
      const first = this.needsActionClaims?.[0] || null;
      const list =
        first?.additionalClaimants || first?.claimDependencies?.additionalClaimants || [];
      return Array.isArray(list) && list.length > 0;
    },
    modalVisible: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      },
    },
  },
  mixins: [formFileUploadMixin],
  methods: {
    onValidationChange(newVal) {
      this.canSubmit = newVal;
    },
    async fetchApprovedClaims() {
      this.isLoading = true;
      try {
        const response = await ProgramClaimService.getProgramClaims({
          formSubmissionId: this.formSubmissionId,
          userId: this.userProfile.userId,
          reviewStatus: "Approved",
        });

        this.approvedClaims = response.rows;
      } catch (error) {
        console.error("Error fetching approved claims", error);
        this.$store.dispatch("showSnackbar", {
          text: "Error fetching approved claims",
          color: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async updateClaim() {
      try {
        await ProgramClaimService.paxUpdateClaim({
          formId: this.needsActionClaims[0].formId,
          formSubmissionId: this.formSubmissionId,
        });
        this.$store.dispatch("showSnackbar", {
          text: "Claim updated successfully",
          color: "success",
        });
      } catch (error) {
        console.error("Error updating claim", error);
        this.$store.dispatch("showSnackbar", {
          text: "Error updating claim",
          color: "error",
        });
      }
    },
    async onSubmit() {
      if (!this.canSubmit) return;
      try {
        this.isSubmitting = true;
        await this.uploadSubmissionFiles(
          this.formSubmissionId,
          this.newAttachments,
          this.updateClaim
        );
        this.$emit("claim-updated");
        this.modalVisible = false;
      } catch (error) {
        console.error("Error updating submission", error);
        this.$store.dispatch("showSnackbar", {
          text: "Error updating submission",
          color: "error",
        });
      } finally {
        this.isSubmitting = false;
      }
    },
  },

  watch: {
    value(newVal, oldVal) {
      if (oldVal && !newVal) {
        this.newAttachments = null;
        this.canSubmit = false;
      }
      if (newVal && !oldVal && !this.approvedClaims) {
        this.fetchApprovedClaims();
      }
    },
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;
  padding: 0;
}
.header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 2rem;
}
.claim-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.items-needing-action {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
  .items-needing-action-header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 0.75rem;
  }
}

.v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;

  .v-expansion-panel-header {
    padding: 1rem;
  }
  .approved-claims {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}
.documentation-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
}
</style>
